import {
  DragEvent,
  MouseEvent as ReactMouseEvent,
  WheelEvent as ReactWheelEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ApiError,
  LiveSession,
  LiveWorkspaceClaimResult,
  LiveWorkspaceHeartbeatResult,
  PagedResult,
  PlayableArea,
  PlayableVideoSource,
  api,
} from '../lib/api';
import { OapmPlayer, PlayerStatus } from '../components/OapmPlayer';
import { useI18n } from '../i18n';

type Tile = {
  source?: PlayableVideoSource;
  session?: LiveSession;
  error?: string;
  loading?: boolean;
};

type ZoomState = {
  scale: number;
  originX: number;
  originY: number;
};

type NvrSourceGroup = {
  key: string;
  name: string;
  items: PlayableVideoSource[];
};

const layoutOptions = [1, 4, 9, 16] as const;
const sourceDragType = 'application/x-oapm-video-source';
const tileDragType = 'application/x-oapm-live-tile';
const liveOpenTimeoutMs = 38_000;
const liveCloseTimeoutMs = 6_000;
const mediaConnectTimeoutMs = 35_000;

function createUuid(): string {
  const cryptoApi = globalThis.crypto;

  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  if (typeof cryptoApi?.getRandomValues !== 'function') {
    return `oapm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}


export function LivePage() {
  const { t } = useI18n();
  const [tileCount, setTileCount] = useState<(typeof layoutOptions)[number]>(16);
  const [tiles, setTiles] = useState<Tile[]>(() => Array.from({ length: 16 }, () => ({})));
  const [areas, setAreas] = useState<PlayableArea[]>([]);
  const [sources, setSources] = useState<PlayableVideoSource[]>([]);
  const [search, setSearch] = useState('');
  const [onlineFilter, setOnlineFilter] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [highlightedTile, setHighlightedTile] = useState<number>();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [nvrBusy, setNvrBusy] = useState<string>();
  const [zoomBySourceId, setZoomBySourceId] = useState<Record<number, ZoomState>>({});
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string>();
  const [layoutPortalTarget, setLayoutPortalTarget] = useState<HTMLElement | null>(null);
  const [noticePortalTarget, setNoticePortalTarget] = useState<HTMLElement | null>(null);
  const clientInstanceId = useMemo(createUuid, []);
  const tilesRef = useRef(tiles);
  const tileCountRef = useRef<(typeof layoutOptions)[number]>(tileCount);
  const slotSyncTimerRef = useRef<number | undefined>(undefined);
  const suppressSourceClickUntilRef = useRef(0);
  const mediaWatchdogsRef = useRef<Map<string, number>>(new Map());
  const mediaEverPlayedRef = useRef<Set<string>>(new Set());
  const reportedMediaFailuresRef = useRef<Set<string>>(new Set());

  function commitTiles(update: (current: Tile[]) => Tile[]): Tile[] {
    const next = update(tilesRef.current);
    tilesRef.current = next;
    setTiles(next);
    return next;
  }

  function clearMediaWatchdog(sessionId: string) {
    const timer = mediaWatchdogsRef.current.get(sessionId);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      mediaWatchdogsRef.current.delete(sessionId);
    }
  }

  function clearMediaTracking(sessionId: string) {
    clearMediaWatchdog(sessionId);
    mediaEverPlayedRef.current.delete(sessionId);
    reportedMediaFailuresRef.current.delete(sessionId);
  }

  function clearAllMediaWatchdogs() {
    for (const timer of mediaWatchdogsRef.current.values()) {
      window.clearTimeout(timer);
    }
    mediaWatchdogsRef.current.clear();
  }

  function reportMediaFailure(
    index: number,
    session: LiveSession,
    errorCode: string,
    message: string,
  ) {
    if (reportedMediaFailuresRef.current.has(session.sessionId)) return;
    reportedMediaFailuresRef.current.add(session.sessionId);
    clearMediaWatchdog(session.sessionId);

    commitTiles((items) => items.map((item, itemIndex) =>
      itemIndex === index && item.session?.sessionId === session.sessionId
        ? { source: item.source, error: message }
        : item));

    void api<void>(
      `/live/sessions/${session.sessionId}/media-failed`,
      {
        method: 'POST',
        body: JSON.stringify({ errorCode, message }),
      },
      {
        timeoutMs: liveCloseTimeoutMs,
        timeoutMessage: t('live.mediaFailureReportTimeout'),
      },
    ).catch(() => {
      // 后端租约与工作区 claim 仍是最终幽灵会话清理兜底。
    });
  }

  function armMediaWatchdog(index: number, session: LiveSession) {
    if (mediaEverPlayedRef.current.has(session.sessionId) ||
        mediaWatchdogsRef.current.has(session.sessionId) ||
        reportedMediaFailuresRef.current.has(session.sessionId)) {
      return;
    }

    const timer = window.setTimeout(() => {
      mediaWatchdogsRef.current.delete(session.sessionId);
      const current = tilesRef.current[index];
      if (current?.session?.sessionId !== session.sessionId ||
          mediaEverPlayedRef.current.has(session.sessionId)) {
        return;
      }

      reportMediaFailure(
        index,
        session,
        'MEDIA_CONNECT_TIMEOUT',
        t('live.mediaTimeout', { seconds: Math.round(mediaConnectTimeoutMs / 1000) }),
      );
    }, mediaConnectTimeoutMs);

    mediaWatchdogsRef.current.set(session.sessionId, timer);
  }

  function handlePlayerStatus(
    index: number,
    session: LiveSession | undefined,
    status: PlayerStatus,
  ) {
    if (!session) return;

    const current = tilesRef.current[index];
    if (current?.session?.sessionId !== session.sessionId) return;

    if (status === 'playing') {
      mediaEverPlayedRef.current.add(session.sessionId);
      clearMediaWatchdog(session.sessionId);
      return;
    }

    if (status === 'error') {
      reportMediaFailure(
        index,
        session,
        'MEDIA_PLAYER_ERROR',
        t('live.mediaPlayerError'),
      );
      return;
    }

    if (status === 'connecting') {
      armMediaWatchdog(index, session);
    }
  }

  useEffect(() => {
    setLayoutPortalTarget(document.getElementById('live-layout-controls'));
    setNoticePortalTarget(document.getElementById('live-notice-slot'));
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(undefined), 3_000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    tilesRef.current = tiles;
  }, [tiles]);

  useEffect(() => {
    tileCountRef.current = tileCount;
  }, [tileCount]);

  useEffect(() => {
    const resetZoomAfterFullscreen = () => {
      if (!document.fullscreenElement) {
        setZoomBySourceId({});
      }
    };
    document.addEventListener('fullscreenchange', resetZoomAfterFullscreen);
    return () => document.removeEventListener('fullscreenchange', resetZoomAfterFullscreen);
  }, []);

  useEffect(() => {
    let disposed = false;
    setWorkspaceReady(false);
    setWorkspaceError(undefined);

    void api<LiveWorkspaceClaimResult>(
      '/live/workspaces/claim',
      {
        method: 'POST',
        body: JSON.stringify({ clientInstanceId }),
      },
      {
        timeoutMs: 8_000,
        timeoutMessage: t('live.workspaceTimeout'),
      },
    )
      .then((result) => {
        if (disposed) return;
        setWorkspaceReady(true);
        if (result.replacedExistingWorkspace || result.closedSessionIds.length > 0) {
          setNotice(t('live.workspaceClaimed'));
        }
      })
      .catch((reason) => {
        if (disposed) return;
        setWorkspaceError(
          reason instanceof ApiError
            ? reason.message
            : t('live.workspaceFailed'),
        );
      });

    return () => {
      disposed = true;
    };
  }, [clientInstanceId]);

  async function loadCatalog() {
    setCatalogLoading(true);
    setCatalogError(undefined);
    try {
      const [areaResult, sourceResult] = await Promise.all([
        api<PlayableArea[]>('/areas'),
        api<PagedResult<PlayableVideoSource>>('/video-sources?page=1&pageSize=200'),
      ]);
      setAreas(areaResult);
      setSources(sourceResult.items);
    } catch (reason) {
      setCatalogError(reason instanceof ApiError ? reason.message : t('live.catalogFailed'));
    } finally {
      setCatalogLoading(false);
    }
  }

  useEffect(() => {
    void loadCatalog();
  }, []);

  useEffect(() => {
    if (!workspaceReady) return;

    let disposed = false;
    let heartbeatInFlight = false;

    const sendWorkspaceHeartbeat = async () => {
      if (disposed || heartbeatInFlight) return;

      const currentSessionIds = Array.from(new Set(
        tilesRef.current.flatMap((tile) =>
          tile.session ? [tile.session.sessionId] : []),
      ));

      heartbeatInFlight = true;
      try {
        const result = await api<LiveWorkspaceHeartbeatResult>(
          '/live/workspaces/heartbeat',
          {
            method: 'POST',
            body: JSON.stringify({ clientInstanceId }),
          },
          {
            timeoutMs: 6_000,
            timeoutMessage: t('live.workspaceHeartbeatTimeout'),
          },
        );
        if (disposed) return;

        if (!result.isOwner) {
          setWorkspaceReady(false);
          setWorkspaceError(t('live.workspaceTaken'));
          clearAllMediaWatchdogs();
          commitTiles((items) => items.map((item) =>
            item.source
              ? { source: item.source, error: t('live.workspaceTakenTile') }
              : {}));
          return;
        }

        const active = new Set(result.activeSessionIds);
        const current = new Set(currentSessionIds);
        const expired = currentSessionIds.filter(
          (sessionId) => !active.has(sessionId));
        const hasPendingOpen = tilesRef.current.some((tile) => tile.loading);
        const unknownServerSessions = hasPendingOpen
          ? []
          : result.activeSessionIds.filter((sessionId) => !current.has(sessionId));

        // 客户端超时后，服务器可能刚好完成建流并留下浏览器未知的会话。
        // 没有正在打开的格子时，主动关闭这些幽灵会话，避免占用容量或触发重复源判断。
        for (const sessionId of unknownServerSessions) {
          void closeSession(sessionId);
        }
        if (unknownServerSessions.length > 0) {
          setNotice(t('live.unknownSessionsCleaned', { count: unknownServerSessions.length }));
        }

        if (expired.length === 0) return;

        const expiredSet = new Set(expired);
        for (const sessionId of expired) {
          clearMediaTracking(sessionId);
        }
        commitTiles((items) => items.map((item) =>
          item.session && expiredSet.has(item.session.sessionId)
            ? { source: item.source, error: t('live.sessionExpired') }
            : item));
      } catch (reason) {
        if (reason instanceof ApiError && reason.status === 401) {
          setNotice(t('live.loginExpired'));
        }
      } finally {
        heartbeatInFlight = false;
      }
    };

    const timer = window.setInterval(() => {
      void sendWorkspaceHeartbeat();
    }, 20_000);
    const resumeHeartbeat = () => {
      if (document.visibilityState === 'visible') {
        void sendWorkspaceHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', resumeHeartbeat);
    window.addEventListener('focus', resumeHeartbeat);
    window.addEventListener('online', resumeHeartbeat);
    window.addEventListener('pageshow', resumeHeartbeat);
    void sendWorkspaceHeartbeat();

    return () => {
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', resumeHeartbeat);
      window.removeEventListener('focus', resumeHeartbeat);
      window.removeEventListener('online', resumeHeartbeat);
      window.removeEventListener('pageshow', resumeHeartbeat);
    };
  }, [clientInstanceId, workspaceReady]);

  useEffect(() => {
    const closeAll = () => {
      for (const tile of tilesRef.current) {
        if (tile.session) {
          void fetch(`/api/live/sessions/${tile.session.sessionId}`, {
            method: 'DELETE',
            credentials: 'include',
            keepalive: true,
          });
        }
      }
    };

    window.addEventListener('beforeunload', closeAll);
    return () => {
      window.removeEventListener('beforeunload', closeAll);
      if (slotSyncTimerRef.current !== undefined) {
        window.clearTimeout(slotSyncTimerRef.current);
      }
      clearAllMediaWatchdogs();
      closeAll();
    };
  }, []);

  const activeSourceIds = useMemo(
    () => new Set(tiles.flatMap((tile) => tile.source ? [tile.source.id] : [])),
    [tiles],
  );

  const groupedSources = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const filtered = sources.filter((source) => {
      if (onlineFilter && source.onlineStatus !== onlineFilter) return false;
      if (!normalizedSearch) return true;
      return [source.displayName, source.areaName, source.nvrDisplayName, source.sourceType]
        .some((value) => value?.toLocaleLowerCase().includes(normalizedSearch));
    });

    const order = new Map(areas.map((area, index) => [area.id, index]));
    const groups = new Map<string, { key: string; name: string; order: number; items: PlayableVideoSource[] }>();
    for (const source of filtered) {
      const key = source.areaId ? String(source.areaId) : 'unclassified';
      const current = groups.get(key) ?? {
        key,
        name: source.areaName ?? t('playback.unclassified'),
        order: source.areaId ? (order.get(source.areaId) ?? 10_000) : 20_000,
        items: [],
      };
      current.items.push(source);
      groups.set(key, current);
    }

    return Array.from(groups.values())
      .sort((left, right) => left.order - right.order || left.name.localeCompare(right.name))
      .map((group) => ({
        ...group,
        items: group.items.sort((left, right) => left.sortOrder - right.sortOrder || left.displayName.localeCompare(right.displayName)),
      }));
  }, [areas, onlineFilter, search, sources]);

  const nvrGroups = useMemo<NvrSourceGroup[]>(() => {
    const groups = new Map<string, NvrSourceGroup>();
    for (const source of sources) {
      const name = source.nvrDisplayName?.trim();
      if (!name) continue;
      const key = name.toLocaleLowerCase();
      const group = groups.get(key) ?? { key, name, items: [] };
      group.items.push(source);
      groups.set(key, group);
    }
    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.sort((left, right) => left.sortOrder - right.sortOrder || left.displayName.localeCompare(right.displayName)),
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [sources]);

  async function closeSession(sessionId: string) {
    clearMediaTracking(sessionId);
    try {
      await api<void>(
        `/live/sessions/${sessionId}`,
        { method: 'DELETE' },
        {
          timeoutMs: liveCloseTimeoutMs,
          timeoutMessage: t('live.releaseTimeout'),
        },
      );
    } catch {
      // 工作区 claim 与后端租约仍是最终清理兜底。
    }
  }

  function closeTile(index: number) {
    const current = tilesRef.current[index];
    commitTiles((items) => items.map((item, itemIndex) =>
      itemIndex === index ? {} : item));

    if (current?.session) {
      void closeSession(current.session.sessionId);
    }
  }

  async function openSource(source: PlayableVideoSource, index: number) {
    const currentTileCount = tileCountRef.current;
    if (index < 0 || index >= currentTileCount) return;
    if (!workspaceReady) {
      setNotice(workspaceError ?? t('live.workspaceNotReady'));
      return;
    }

    const duplicateIndex = tilesRef.current
      .slice(0, currentTileCount)
      .findIndex((tile) => tile.source?.id === source.id);
    if (duplicateIndex >= 0 && duplicateIndex !== index) {
      setHighlightedTile(duplicateIndex);
      setNotice(t('live.alreadyInTile', { name: source.displayName, index: duplicateIndex + 1 }));
      window.setTimeout(() => setHighlightedTile(undefined), 1_800);
      return;
    }
    if (duplicateIndex === index) {
      setHighlightedTile(index);
      setNotice(t('live.alreadyAssigned', { name: source.displayName, index: index + 1 }));
      window.setTimeout(() => setHighlightedTile(undefined), 1_800);
      return;
    }

    const previous = tilesRef.current[index];
    commitTiles((items) => items.map((tile, tileIndex) =>
      tileIndex === index
        ? { source, loading: true }
        : tile));

    if (previous?.session) {
      void closeSession(previous.session.sessionId);
    }

    setNotice(undefined);

    try {
      const session = await api<LiveSession>(
        '/live/sessions',
        {
          method: 'POST',
          body: JSON.stringify({
            videoSourceId: source.id,
            clientInstanceId,
            slotIndex: index,
          }),
        },
        {
          timeoutMs: liveOpenTimeoutMs,
          timeoutMessage: t('live.startTimeout'),
        },
      );

      const stillAssigned =
        tilesRef.current[index]?.source?.id === source.id;
      if (!stillAssigned) {
        void closeSession(session.sessionId);
        return;
      }

      reportedMediaFailuresRef.current.delete(session.sessionId);
      mediaEverPlayedRef.current.delete(session.sessionId);
      commitTiles((items) => items.map((tile, tileIndex) =>
        tileIndex === index && tile.source?.id === source.id
          ? { source, session }
          : tile));
      armMediaWatchdog(index, session);
    } catch (reason) {
      const message = reason instanceof ApiError
        ? reason.message
        : t('live.startFailed');
      commitTiles((items) => items.map((tile, tileIndex) =>
        tileIndex === index && tile.source?.id === source.id
          ? { source, error: message }
          : tile));
    }
  }

  async function addToFirstEmpty(source: PlayableVideoSource) {
    const index = tilesRef.current
      .slice(0, tileCountRef.current)
      .findIndex((tile) => !tile.source);
    if (index < 0) {
      setNotice(t('live.noEmptyTile'));
      return;
    }
    await openSource(source, index);
  }

  async function playNvrGroup(group: NvrSourceGroup) {
    if (nvrBusy) return;
    if (!workspaceReady) {
      setNotice(workspaceError ?? t('live.workspaceNotReady'));
      return;
    }

    const selected = group.items.slice(0, 16);
    const alreadyAssigned = tileCountRef.current === 16 &&
      selected.length > 0 &&
      selected.every((source, index) => {
        const tile = tilesRef.current[index];
        return tile?.source?.id === source.id &&
          Boolean(tile.session) &&
          !tile.loading &&
          !tile.error;
      });

    if (alreadyAssigned) {
      setNotice(t('live.nvrAlreadyRunning', { name: group.name, count: selected.length }));
      return;
    }

    setNvrBusy(group.key);
    setNotice(t('live.nvrStarting', { name: group.name, count: selected.length }));
    try {
      const currentSessions = tilesRef.current.flatMap((tile) =>
        tile.session ? [tile.session.sessionId] : []);
      commitTiles(() => Array.from({ length: 16 }, () => ({})));
      for (const sessionId of currentSessions) {
        void closeSession(sessionId);
      }

      tileCountRef.current = 16;
      setTileCount(16);

      await Promise.all(selected.map((source, index) => openSource(source, index)));
      setNotice(t('live.nvrSubmitted', { name: group.name, count: selected.length }));
    } finally {
      setNvrBusy(undefined);
    }
  }

  async function reconnectTile(index: number) {
    const source = tilesRef.current[index]?.source;
    if (!source) return;
    closeTile(index);
    await openSource(source, index);
  }

  async function changeLayout(next: (typeof layoutOptions)[number]) {
    const snapshot = tilesRef.current;
    const sessionsToClose = snapshot.slice(next).flatMap((tile) =>
      tile.session ? [tile.session.sessionId] : []);
    commitTiles((items) => items.map((item, index) =>
      index >= next ? {} : item));
    for (const sessionId of sessionsToClose) {
      void closeSession(sessionId);
    }
    tileCountRef.current = next;
    setTileCount(next);
  }

  async function updateSessionSlot(tile: Tile | undefined, slotIndex: number) {
    if (!tile?.session) return;
    await api<void>(
      `/live/sessions/${tile.session.sessionId}/slot`,
      {
        method: 'PUT',
        body: JSON.stringify({ slotIndex }),
      },
      {
        timeoutMs: 5_000,
        timeoutMessage: t('live.positionSyncTimeout'),
      },
    );
  }

  function scheduleSlotSync(snapshot: Tile[]) {
    if (slotSyncTimerRef.current !== undefined) {
      window.clearTimeout(slotSyncTimerRef.current);
    }
    slotSyncTimerRef.current = window.setTimeout(() => {
      const assignments = snapshot
        .slice(0, tileCountRef.current)
        .flatMap((tile, index) => tile.session ? [{ tile, index }] : []);
      void Promise.all(assignments.map(({ tile, index }) => updateSessionSlot(tile, index)))
        .catch((reason) => {
          if (reason instanceof ApiError && reason.status === 404) return;
          setNotice(reason instanceof ApiError
            ? `画面已交换，但会话位置记录稍后同步失败：${reason.message}`
            : '画面已交换，但会话位置记录稍后同步失败。');
        });
    }, 300);
  }

  function swapTiles(from: number, to: number) {
    const currentTileCount = tileCountRef.current;
    if (from === to || from < 0 || to < 0 || from >= currentTileCount || to >= currentTileCount) return;
    const next = [...tilesRef.current];
    [next[from], next[to]] = [next[to] ?? {}, next[from] ?? {}];
    tilesRef.current = next;
    setTiles(next);
    scheduleSlotSync(next);
  }

  function startSourceDrag(event: DragEvent, sourceId: number) {
    suppressSourceClickUntilRef.current = Date.now() + 600;
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData(sourceDragType, String(sourceId));
  }

  function finishSourceDrag() {
    suppressSourceClickUntilRef.current = Date.now() + 250;
  }

  function clickSource(source: PlayableVideoSource) {
    if (Date.now() < suppressSourceClickUntilRef.current) return;
    void addToFirstEmpty(source);
  }

  function startTileDrag(event: DragEvent, index: number) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(tileDragType, String(index));
  }

  function dropOnTile(event: DragEvent, index: number) {
    event.preventDefault();
    const sourceId = Number(event.dataTransfer.getData(sourceDragType));
    if (sourceId > 0) {
      const source = sources.find((item) => item.id === sourceId);
      if (source) void openSource(source, index);
      return;
    }

    const from = Number(event.dataTransfer.getData(tileDragType));
    if (Number.isInteger(from)) {
      swapTiles(from, index);
    }
  }

  function toggleGroup(key: string) {
    setCollapsedGroups((current) => ({ ...current, [key]: !current[key] }));
  }

  function navigateFullscreen(index: number, direction: -1 | 1) {
    const occupied = tilesRef.current
      .slice(0, tileCountRef.current)
      .flatMap((tile, tileIndex) => tile.source ? [tileIndex] : []);
    if (occupied.length <= 1) return;

    const currentPosition = occupied.indexOf(index);
    const basePosition = currentPosition >= 0 ? currentPosition : 0;
    const nextPosition = (basePosition + direction + occupied.length) % occupied.length;
    const targetIndex = occupied[nextPosition];
    if (targetIndex !== undefined && targetIndex !== index) {
      swapTiles(index, targetIndex);
    }
  }

  function fullscreen(index: number) {
    const element = document.getElementById(`live-tile-${index}`);
    if (element?.requestFullscreen) {
      void element.requestFullscreen();
    }
  }

  function fullscreenOnDoubleClick(event: ReactMouseEvent<HTMLElement>, index: number) {
    if ((event.target as HTMLElement).closest('button, details, summary, input, select')) return;
    if (tilesRef.current[index]?.source) fullscreen(index);
  }

  function zoomFullscreen(event: ReactWheelEvent<HTMLElement>, index: number) {
    const element = document.getElementById(`live-tile-${index}`);
    const source = tilesRef.current[index]?.source;
    if (!element || !source || document.fullscreenElement !== element) return;

    event.preventDefault();
    const current = zoomBySourceId[source.id] ?? { scale: 1, originX: 50, originY: 50 };
    const direction = event.deltaY < 0 ? 0.25 : -0.25;
    const scale = Math.min(4, Math.max(1, Number((current.scale + direction).toFixed(2))));
    const rect = element.getBoundingClientRect();
    const originX = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const originY = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    setZoomBySourceId((state) => ({ ...state, [source.id]: { scale, originX, originY } }));
  }

  return (
    <section className="page-section live-page">
      {layoutPortalTarget && createPortal(
        <div className="layout-switcher topbar-layout-switcher">
          {layoutOptions.map((count) => (
            <button
              key={count}
              className={tileCount === count ? 'active' : ''}
              onClick={() => void changeLayout(count)}
              aria-label={t('live.layoutLabel', { columns: Math.sqrt(count) })}
              title={t('live.layoutLabel', { columns: Math.sqrt(count) })}
            >
              {Math.sqrt(count)}×{Math.sqrt(count)}
            </button>
          ))}
        </div>,
        layoutPortalTarget,
      )}

      {notice && noticePortalTarget && createPortal(
        <div className="notice success-notice live-notice" onClick={() => setNotice(undefined)}>
          {notice}
        </div>,
        noticePortalTarget,
      )}
      {workspaceError && <div className="notice live-notice live-workspace-error">{workspaceError}</div>}

      <div className="live-workspace">
        <aside className="live-source-panel">
          <div className="source-panel-header">
            <div><strong>{t('live.batchTitle')}</strong><span>{t('live.importedCount', { count: sources.length })}</span></div>
            <button onClick={() => void loadCatalog()} disabled={catalogLoading}>{t('common.refresh')}</button>
          </div>
          {nvrGroups.length > 0 && (
            <div className="nvr-quick-play">
              {nvrGroups.map((group) => (
                <button
                  key={group.key}
                  title={t('live.sourceGroupHint', { count: group.items.length })}
                  disabled={Boolean(nvrBusy) || !workspaceReady}
                  onClick={() => void playNvrGroup(group)}
                >
                  <span>▶ {group.name}</span><small>{group.items.length}</small>
                </button>
              ))}
            </div>
          )}
          <div className="source-panel-filters">
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('live.search')} />
            <select value={onlineFilter} onChange={(event) => setOnlineFilter(event.target.value)}>
              <option value="">{t('live.allStatus')}</option>
              <option value="ONLINE">{t('common.online')}</option>
              <option value="OFFLINE">{t('common.offline')}</option>
              <option value="UNKNOWN">{t('common.unknown')}</option>
            </select>
          </div>
          {catalogError && <div className="source-panel-error">{catalogError}</div>}
          <div className="source-panel-header source-panel-subheader">
            <div>
              <strong>{t('live.singleTitle')}</strong>
              <span>{t('live.singleCount', { count: groupedSources.reduce((total, group) => total + group.items.length, 0) })}</span>
            </div>
          </div>
          <div className="source-groups">
            {catalogLoading && <p className="muted source-panel-message">{t('live.loadingCatalog')}</p>}
            {!catalogLoading && groupedSources.length === 0 && <p className="muted source-panel-message">{t('live.noSources')}</p>}
            {groupedSources.map((group) => (
              <section className="source-group" key={group.key}>
                <button className="source-group-title" onClick={() => toggleGroup(group.key)}>
                  <span>{collapsedGroups[group.key] ? '▸' : '▾'} {group.name}</span>
                  <small>{group.items.length}</small>
                </button>
                {!collapsedGroups[group.key] && group.items.map((source) => {
                  const active = activeSourceIds.has(source.id);
                  return (
                    <button
                      className={`source-list-item ${active ? 'active' : ''}`}
                      key={source.id}
                      draggable={workspaceReady}
                      disabled={!workspaceReady}
                      onDragStart={(event) => startSourceDrag(event, source.id)}
                      onDragEnd={finishSourceDrag}
                      onClick={() => clickSource(source)}
                      title={t('live.dragTitle')}
                    >
                      <span className={`source-state-dot ${source.onlineStatus === 'ONLINE' ? 'online' : 'offline'}`} />
                      <span className="source-list-copy">
                        <strong>{source.displayName}</strong>
                      </span>
                    </button>
                  );
                })}
              </section>
            ))}
          </div>
        </aside>

        <div className={`video-grid grid-${tileCount}`}>
          {tiles.slice(0, tileCount).map((tile, index) => {
            const zoom = tile.source ? zoomBySourceId[tile.source.id] : undefined;
            return (
              <article
                id={`live-tile-${index}`}
                className={`video-tile ${highlightedTile === index ? 'highlighted' : ''} ${tile.source ? 'occupied' : ''}`}
                key={`tile-${index}`}
                draggable={Boolean(tile.source)}
                onDragStart={(event) => startTileDrag(event, index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => dropOnTile(event, index)}
                onDoubleClick={(event) => fullscreenOnDoubleClick(event, index)}
                onWheel={(event) => zoomFullscreen(event, index)}
              >
                <div className="tile-header">
                  <div className="tile-title">
                    <span>{tile.source?.displayName ?? t('live.tileName', { index: String(index + 1).padStart(2, '0') })}</span>
                    {tile.source && <small>{tile.source.onlineStatus === 'ONLINE' ? t('common.online') : tile.source.onlineStatus === 'OFFLINE' ? t('common.offline') : t('common.unknown')}</small>}
                    {zoom && zoom.scale > 1 && <small className="tile-zoom-value">{zoom.scale.toFixed(2)}×</small>}
                  </div>
                  <div className="tile-actions">
                    {tile.source && <button onClick={() => void reconnectTile(index)}>{t('live.reconnect')}</button>}
                    {tile.source && <button onClick={() => fullscreen(index)}>{t('common.fullscreen')}</button>}
                    {tile.source && <button onClick={() => void closeTile(index)}>{t('live.close')}</button>}
                  </div>
                </div>
                <div
                  className="tile-zoom-layer"
                  style={zoom ? {
                    transform: `scale(${zoom.scale})`,
                    transformOrigin: `${zoom.originX}% ${zoom.originY}%`,
                  } : undefined}
                >
                  <OapmPlayer
                    url={tile.session?.mediaUrl}
                    isLive
                    label={tile.source?.displayName}
                    videoOnly
                    mediaShardKey={tile.source?.id}
                    onStatusChange={(status) =>
                      handlePlayerStatus(index, tile.session, status)}
                    onPrevious={() => navigateFullscreen(index, -1)}
                    onNext={() => navigateFullscreen(index, 1)}
                    previousDisabled={tilesRef.current.filter((item) => item.source).length <= 1}
                    nextDisabled={tilesRef.current.filter((item) => item.source).length <= 1}
                  />
                </div>
                {!tile.source && <div className="tile-empty-state"><strong>{t('live.emptyTitle')}</strong><span>{t('live.emptyHint')}</span></div>}
                {tile.loading && <div className="tile-overlay">{t('live.loadingStream')}</div>}
                {tile.error && <div className="tile-overlay error-overlay"><span>{tile.error}</span><button onClick={() => void reconnectTile(index)}>{t('common.retry')}</button></div>}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
